import * as Yup from "yup";
import { EVENT_TYPES, TOURNAMENT_TYPES, minUserNameLength } from "./constants";
import dayjs from "dayjs";
import moment from "moment";

// login validations
export const loginValidation = (signInPhone) =>
  Yup.object().shape({
    email: signInPhone
      ? Yup.string()
      : Yup.string().email("Enter valid email address").required("Email is required"),
    password: Yup.string().required("Password is required"),
    phoneNumber: signInPhone
      ? Yup.string()
          .min(10, "Phone number must at least be 10 digits")
          .required("Phone number is required")
      : Yup.string().notRequired()
  });

// signUp validations
export const signUpValidations = (isSponsor) =>
  Yup.object().shape({
    firstName: Yup.string().required("This field is required"),
    lastName: isSponsor ? Yup.string() : Yup.string().required("This field is required"),
    nickName: Yup.string(),
    email: Yup.string().email("Enter valid email address").required("Email is required"),
    password: Yup.string()
      .min(7, "Password length must be greater than 7 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .required("Please confirm your password")
      .oneOf([Yup.ref("password"), null], "Passwords must match"),
    phoneNumber: Yup.string()
      .min(10, "Phone number must at least be 10 digits")
      .required("Phone number is required"),
    termsAndConditions: Yup.boolean()
      .required()
      .test("is-true", (value) => value === true),
    DOB: Yup.string()
      .test("is-age-valid", "People older than 10 years can create signup only", function (DOB) {
        const todayDate = new Date();
        const year = todayDate.getFullYear();
        const choosedDate = new Date(DOB);
        const choosedYear = choosedDate.getFullYear();
        let age = year - choosedYear;
        if (age < 10) {
          return false;
        } else if (
          age === 10 &&
          todayDate.getMonth() <= choosedDate.getMonth() &&
          choosedDate.getDate() + 1 < todayDate.getDate()
        ) {
          return false;
        }
        return true;
      })
      .required("This field is required")
  });

export const sponsorSignUpValidations = () =>
  Yup.object().shape({
    firstName: Yup.string().required("This field is required"),
    lastName: Yup.string(),
    email: Yup.string().email("Enter valid email address").required("Email is required"),
    password: Yup.string()
      .min(7, "Password length must be greater than 7 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .required("Please confirm your password")
      .oneOf([Yup.ref("password"), null], "Passwords must match"),
    phoneNumber: Yup.string()
      .min(10, "Phone number must at least be 10 digits")
      .required("Phone number is required"),
    documentVerification: Yup.mixed().test(
      "fileRequired",
      "Document verification is required",
      function (value) {
        return value && value.fileList && value.fileList.length > 0;
      }
    ),
    termsAndConditions: Yup.boolean()
      .required()
      .test("is-true", "You must accept the terms and conditions", (value) => value === true),
    DOB: Yup.string().when("sponsorType", {
      is: (val) => val !== "organization",
      then: () =>
        Yup.string()
          .test(
            "is-age-valid",
            "People older than 10 years can create signup only",
            function (DOB) {
              if (!DOB) return false;
              const todayDate = new Date();
              const year = todayDate.getFullYear();
              const choosedDate = new Date(DOB);
              const choosedYear = choosedDate.getFullYear();
              let age = year - choosedYear;
              if (age < 10) return false;
              if (
                age === 10 &&
                todayDate.getMonth() <= choosedDate.getMonth() &&
                choosedDate.getDate() + 1 < todayDate.getDate()
              ) {
                return false;
              }
              return true;
            }
          )
          .required("This field is required"),
      otherwise: () => Yup.string().notRequired()
    }),

    sponsorType: Yup.string().required("Sponsor type is required")
  });

// create profile: Player
export const createPlayerProfileValidations = (isEdit) =>
  Yup.object().shape({
    firstName: isEdit ? Yup.string() : Yup.string().required("This field is required"),
    lastName: isEdit ? Yup.string() : Yup.string().required("This field is required"),
    name: isEdit ? Yup.string().required("This field is required") : Yup.string(),
    nickName: Yup.string(),
    username: Yup.string()
      .min(minUserNameLength, `Username must be at least ${minUserNameLength} characters`)
      .required("User name is required") // This makes the field required and provides a custom error message.
      .matches(
        /^[a-zA-Z_][a-zA-Z0-9_]*$/, // This regular expression ensures the username starts with a letter or underscore and can be followed by letters, numbers, or underscores.
        "Username can only contain letters, numbers, and underscores, without spaces, and cannot start with a number" // Custom error message for when the username does not match the pattern.
      ),
    location: Yup.string().required("This field is required"),
    DOB: Yup.string()
      .test(
        "is-age-valid",
        "People older than 10 years can create organizer profile only",
        function (DOB) {
          const todayDate = new Date();
          const year = todayDate.getFullYear();
          const choosedDate = new Date(DOB);
          const choosedYear = choosedDate.getFullYear();
          let age = year - choosedYear;
          if (age < 10) {
            return false;
          } else if (
            age === 10 &&
            todayDate.getMonth() <= choosedDate.getMonth() &&
            choosedDate.getDate() + 1 < todayDate.getDate()
          ) {
            return false;
          }
          return true;
        }
      )
      .required("This field is required"),
    selectedSport: Yup.string().required("This field is required")
    // experience: Yup.object()
    //   .shape({
    //     name: Yup.string(),
    //     date: Yup.string(),
    //     location: Yup.string(),
    //     teamName: Yup.string(),
    //     role: Yup.string(),
    //     awards: Yup.string(),
    //     description: Yup.string(),
    //     pictures: Yup.object()
    //   })
    //   .test(
    //     "experience-required",
    //     "Please add All the fields of Experience",
    //     function (experience) {
    //       if (experience.name) {
    //         if (
    //           !experience.date ||
    //           !experience.location ||
    //           !experience.teamName ||
    //           !experience.role ||
    //           !experience.description ||
    //           !experience.pictures?.preview
    //         ) {
    //           return false;
    //         }
    //       }
    //       return true;
    //     }
    //   )
  });

// create profile: Organizer
export const createPlayerOrganizerValidations = (isEdit) =>
  Yup.object().shape({
    firstName: isEdit ? Yup.string() : Yup.string().required("This field is required"),
    lastName: isEdit ? Yup.string() : Yup.string().required("This field is required"),
    name: isEdit ? Yup.string().required("This field is required") : Yup.string(),
    username: Yup.string()
      .min(minUserNameLength, `Username must be at least ${minUserNameLength} characters`)
      .required("User name is required") // This makes the field required and provides a custom error message.
      .matches(
        /^[a-zA-Z_][a-zA-Z0-9_]*$/, // This regular expression ensures the username starts with a letter or underscore and can be followed by letters, numbers, or underscores.
        "Username can only contain letters, numbers, and underscores, without spaces, and cannot start with a number" // Custom error message for when the username does not match the pattern.
      ),
    location: Yup.string().required("This field is required"),
    DOB: Yup.string()
      .test(
        "is-age-valid",
        "People older than 10 years can create organizer profile only",
        function (DOB) {
          const todayDate = new Date();
          const year = todayDate.getFullYear();
          const choosedDate = new Date(DOB);
          const choosedYear = choosedDate.getFullYear();
          let age = year - choosedYear;
          if (age < 10) {
            return false;
          } else if (
            age === 10 &&
            todayDate.getMonth() <= choosedDate.getMonth() &&
            choosedDate.getDate() + 1 < todayDate.getDate()
          ) {
            return false;
          }
          return true;
        }
      )
      .required("This field is required")
    // experience: Yup.object()
    //   .shape({
    //     name: Yup.string(),
    //     date: Yup.string(),
    //     location: Yup.string(),
    //     teamName: Yup.string(),
    //     role: Yup.string(),
    //     awards: Yup.string(),
    //     description: Yup.string(),
    //     pictures: Yup.object()
    //   })
    //   .test(
    //     "experience-required",
    //     "Please add All the fields of Experience",
    //     function (experience) {
    //       if (experience.name) {
    //         if (
    //           !experience.date ||
    //           !experience.location ||
    //           !experience.teamName ||
    //           !experience.role ||
    //           !experience.description ||
    //           !experience.pictures?.preview
    //         ) {
    //           return false;
    //         }
    //       }
    //       return true;
    //     }
    //   )
  });

// create team validations
export const createTeamValidations = () =>
  Yup.object().shape({
    name: Yup.string().required("This field is required"),
    username: Yup.string()
      .min(minUserNameLength, `Username must be at least ${minUserNameLength} characters`)
      .required("User name is required") // This makes the field required and provides a custom error message.
      .matches(
        /^[a-zA-Z_][a-zA-Z0-9_]*$/, // This regular expression ensures the username starts with a letter or underscore and can be followed by letters, numbers, or underscores.
        "Username can only contain letters, numbers, and underscores, without spaces, and cannot start with a number" // Custom error message for when the username does not match the pattern.
      ),
    description: Yup.string(),
    sportsType: Yup.string().required("This field is required"),
    selectedSport: Yup.string().required("This field is required"),
    location: Yup.string().required("This field is required"),
    establishedDate: Yup.string().required("This field is required")
  });

// create panel validations
export const createPanelValidations = () =>
  Yup.object().shape({
    name: Yup.string().required("This field is required"),
    username: Yup.string()
      .min(minUserNameLength, `Username must be at least ${minUserNameLength} characters`)
      .required("User name is required") // This makes the field required and provides a custom error message.
      .matches(
        /^[a-zA-Z_][a-zA-Z0-9_]*$/, // This regular expression ensures the username starts with a letter or underscore and can be followed by letters, numbers, or underscores.
        "Username can only contain letters, numbers, and underscores, without spaces, and cannot start with a number" // Custom error message for when the username does not match the pattern.
      ),
    description: Yup.string(),
    establishedDate: Yup.string().required("This field is required"),
    location: Yup.string().required("This field is required")
  });

// create event validations

export const createEventValidations = () =>
  Yup.object().shape({
    name: Yup.string().required("This field is required"),
    description: Yup.string(),
    location: Yup.string().when("type", {
      is: (type) => type !== EVENT_TYPES.SCRIM,
      then: () => Yup.string().required("Location is required for tournaments"),
      otherwise: () => Yup.string()
    }),
    city: Yup.string().when("type", {
      is: (type) => type !== EVENT_TYPES.SCRIM,
      then: () => Yup.string().required("City is required for tournaments"),
      otherwise: () => Yup.string()
    }),
    sportsType: Yup.string().required("Sports type is required first"),
    selectedSport: Yup.string().required("This field is required"),
    type: Yup.string().when("selectedSport", {
      is: (selectedSport) => selectedSport?.toLowerCase() === "pubg",
      then: () => Yup.string().required("Event type is required for PUBG"),
      otherwise: () => Yup.string()
    }),
    startDate: Yup.string().required("Start date is required"),
    startTime: Yup.string().when("type", {
      is: EVENT_TYPES.SCRIM,
      then: () => Yup.string().required("Start time is required for scrim events"),
      otherwise: () => Yup.string()
    }),
    endDate: Yup.string().when("type", {
      is: (type) => type !== EVENT_TYPES.SCRIM,
      then: () =>
        Yup.string()
          .required("Select End Date")
          .test("end-date-validation", function (endDate) {
            const { startDate } = this.parent;
            if (!startDate || !endDate) return true;

            if (new Date(endDate) <= new Date(startDate)) {
              return this.createError({
                message: "Ending Date should be greater than the Starting Date"
              });
            }
            return true;
          }),
      otherwise: () => Yup.string()
    }),
    deadLineDate: Yup.string()
      .required("Deadline date is required")
      .test("deadline-validation", function (deadLineDate) {
        const { startDate, type } = this.parent;
        const isStartDate = dayjs(startDate).isValid();

        if (!isStartDate || !deadLineDate) return true;

        if (type === EVENT_TYPES.SCRIM) {
          if (new Date(deadLineDate) > new Date(startDate)) {
            return this.createError({
              message: "Deadline should be on or before event date"
            });
          }
          return true;
        }

        if (new Date(deadLineDate) >= new Date(startDate)) {
          return this.createError({
            message: "Deadline should be less than starting date"
          });
        }

        return true;
      }),
    deadlineTime: Yup.string().when("type", {
      is: EVENT_TYPES.SCRIM,
      then: () => Yup.string().required("Deadline time is required for scrim events"),
      otherwise: () => Yup.string()
    }),

    scrimDateTime: Yup.mixed().when(
      ["type", "startDate", "startTime", "deadLineDate", "deadlineTime"],
      {
        is: (type, startDate, startTime, deadLineDate, deadlineTime) =>
          type === EVENT_TYPES.SCRIM && startDate && startTime && deadLineDate && deadlineTime,
        then: () =>
          Yup.mixed().test({
            name: "scrim-datetime-validation",
            message: "Event time must be after registration deadline time on the same day",
            test: function () {
              const { startDate, startTime, deadLineDate, deadlineTime } = this.parent;

              if (moment(startDate).isSame(moment(deadLineDate), "day")) {
                const eventDateTime = moment(`${startDate} ${startTime}`, "YYYY-MM-DD hh:mm a");
                const deadlineDateTime = moment(
                  `${deadLineDate} ${deadlineTime}`,
                  "YYYY-MM-DD hh:mm a"
                );

                return eventDateTime.isAfter(deadlineDateTime);
              }

              return true;
            }
          }),
        otherwise: () => Yup.mixed()
      }
    ),
    fee: Yup.number().min(0, "Fee can not be a negative value"),
    awards: Yup.array().min(1, "At least one is required").required("This field is required"),
    maxParticipants: Yup.number().test("max-participants-validation", function (maxParticipants) {
      const { minParticipants } = this.parent;

      if (maxParticipants === undefined || minParticipants === undefined) {
        return true;
      }

      if (maxParticipants < minParticipants) {
        return this.createError({
          message: "Max Participants should be greater than or equal to minimum Participants"
        });
      }

      return true;
    }),
    minParticipants: Yup.number().test("min-participants-validation", function (minParticipants) {
      const { typeOfMatch } = this.parent;

      if (typeOfMatch?.[0] === TOURNAMENT_TYPES.doubleElimination) {
        if (!minParticipants || minParticipants < 4) {
          return this.createError({
            message: "Minimum Participants is required and should be 4"
          });
        }
      } else {
        if (!minParticipants || minParticipants < 2) {
          return this.createError({
            message: "Minimum Participants is required and should be 2"
          });
        }
      }

      return true;
    }),
    typeOfMatch: Yup.string().required("This field is required"),
    rules: Yup.string().when(["rulesFile"], {
      is: (rulesFile) => !rulesFile || !rulesFile?.file,
      then: () => Yup.string().required("This field is required"),
      otherwise: () => Yup.string()
    }),
    rulesFile: Yup.mixed().nullable(),
    panel: Yup.string().required("This field is required"),
    coordinates: Yup.object().when("type", {
      is: (type) => type !== EVENT_TYPES.SCRIM,
      then: () =>
        Yup.object().shape({
          latitude: Yup.string().required("Latitude is required"),
          longitude: Yup.string().required("Longitude is required")
        }),
      otherwise: () => Yup.object()
    }),
    wildCardEntry: Yup.boolean(),
    wildCard: Yup.array().when(["wildCardEntry", "type"], {
      is: (wildCardEntry, type) => wildCardEntry === true && type !== EVENT_TYPES.SCRIM,
      then: (schema) =>
        schema
          .of(
            Yup.object().shape({
              name: Yup.string().required("Wild Card Name is required"),
              registrationFee: Yup.number()
                .typeError("Entry Fee must be a number")
                .positive("Entry Fee must be a positive number")
                .required("Entry Fee is required"),
              noOfSlots: Yup.number()
                .typeError("No of slots must be a number")
                .integer("No of slots must be an integer")
                .positive("No of slots must be a positive number")
                .required("No of slots is required")
            })
          )
          .min(1, "At least one entry is required ")
    })
  });

// create sponsor
export const createSponsorProfileValidations = (isOrganization) =>
  Yup.object().shape({
    name: Yup.string().required("This field is required"),
    location: Yup.string().required("This field is required"),
    DOB: !isOrganization
      ? Yup.string()
          .required("This field is required")
          .test(
            "is-age-valid",
            "People older than 10 years can create signup only",
            function (DOB) {
              if (!DOB) return false;
              const todayDate = new Date();
              const year = todayDate.getFullYear();
              const choosedDate = new Date(DOB);
              const choosedYear = choosedDate.getFullYear();
              let age = year - choosedYear;
              if (age < 10) return false;
              if (
                age === 10 &&
                todayDate.getMonth() <= choosedDate.getMonth() &&
                choosedDate.getDate() + 1 < todayDate.getDate()
              ) {
                return false;
              }
              return true;
            }
          )
      : Yup.string().nullable()
  });

export const createCampaignValidations = () =>
  Yup.object().shape({
    name: Yup.string().required(),
    description: Yup.string().required()
  });

export const createCampaignActivityValidations = () =>
  Yup.object().shape({
    name: Yup.string().required("This field is required"),
    sportType: Yup.string(),
    activityType: Yup.string().required("This field is required"),
    date: Yup.string()
  });

export const withdrawalFormValidations = () =>
  Yup.object().shape({
    accountNumber: Yup.string().required("Account number is required"),
    accountTitle: Yup.string().required("Bank title is required"),
    bankName: Yup.string().required("Bank name is required"),
    amount: Yup.number()
      .required("Amount is required")
      .min(0, "Amount must be greater than or equal to 0"),
    mobileNumber: Yup.string(),
    fileList: Yup.array().nullable()
  });

export const knockoutScheduleFormValidations = () =>
  Yup.object().shape({
    playhours: Yup.string()
      .required("This field is required")
      .test(
        "playhours-greater-than-timeforonematch-and-breaktime",
        "Play hours must be greater than time for one match and break time",
        function (value) {
          const { timeforonematch, breaktime } = this.parent; // Access sibling field values
          if (value && timeforonematch && breaktime) {
            // Convert playhours, timeforonematch, and breaktime to Date objects
            const [playHours, playMinutes] = value.split(":").map(Number);
            const [matchHours, matchMinutes] = timeforonematch.split(":").map(Number);
            const [breakHours, breakMinutes] = breaktime.split(":").map(Number);

            const playTime = new Date(0, 0, 0, playHours, playMinutes);
            const matchTime = new Date(0, 0, 0, matchHours, matchMinutes);
            const breakTime = new Date(0, 0, 0, breakHours, breakMinutes);

            // Check if playhours is greater than both timeforonematch and breaktime
            if (playTime <= matchTime || playTime <= breakTime) {
              return false; // Validation fails if playTime is not greater than either matchTime or breakTime
            }
          }
          return true; // Validation passes if the condition is met
        }
      ),
    breaktime: Yup.string().required("This field is required"),
    timeforonematch: Yup.string().required("This field is required"),
    startfirstmatch: Yup.date().required("This field is required")
  });
